/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useCallback, useMemo } from 'react';

export interface UpdateFilters<T extends Record<string, any>> {
  (newFilters: Partial<T>, options?: { reset?: boolean; skipEmpty?: boolean }): void;
}

interface UseFiltersOptions<T extends Record<string, any>> {
  defaultFilters: T;
  saveToUrl?: boolean;
}

function useFilters<T extends Record<string, any>>({
  defaultFilters,
  saveToUrl = false,
}: UseFiltersOptions<T>) {
  const [filters, setFilters] = useState<T>(() => {
    if (saveToUrl) {
      const urlParams = new URLSearchParams(window.location.search);
      const urlFilters: Partial<T> = {};
      Object.keys(defaultFilters).forEach((key) => {
        let value;
        value = urlParams.get(key);
        if (value === 'true') {
          value = true;
        } else if (value === 'false') {
          value = false;
        }
        if (value !== null) {
          urlFilters[key as keyof T] = value as T[keyof T];
        }
      });
      return { ...defaultFilters, ...urlFilters } as T;
    }
    return defaultFilters as T;
  });

  const updateFilters: UpdateFilters<T> = useCallback((newFilters: Partial<T>, options) => {
    setFilters((prevFilters) => {
      const filtersToAdd = !options?.skipEmpty
        ? newFilters
        : Object.entries(newFilters).reduce((acc, [key, value]) => {
            if (value !== undefined && value !== null) {
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              acc[key] = value;
            }
            return acc;
          }, {} as Partial<T>);

      return {
        ...(options?.reset ? ({} as T) : prevFilters),
        ...filtersToAdd,
      };
    });
  }, []);

  useEffect(() => {
    if (saveToUrl) {
      const currentUrlParams = new URLSearchParams(window.location.search);

      const preservedParams = new URLSearchParams();
      Array.from(currentUrlParams.keys()).forEach((key) => {
        if (!(key in defaultFilters)) {
          const value = currentUrlParams.get(key);
          if (value !== null && value !== '') {
            preservedParams.set(key, value);
          }
        }
      });

      Object.entries(filters).forEach(([key, value]) => {
        if (
          key in defaultFilters &&
          value !== undefined &&
          value !== null &&
          value !== ''
        ) {
          preservedParams.set(key, String(value));
        }
      });

      const newQuery = preservedParams.toString();
      const newUrl = `${window.location.pathname}${newQuery ? '?' + newQuery : ''}`;
      window.history.pushState({}, '', newUrl);
    }
  }, [filters, saveToUrl, defaultFilters]);

  const resetFilters = useCallback(() => {
    setFilters(defaultFilters as T);
  }, [defaultFilters]);

  const nonEmptyFilters = useMemo(() => {
    return Object.entries(filters).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        acc[key] = value;
      }
      return acc;
    }, {} as Partial<T>);
  }, [filters]);

  return {
    filters,
    updateFilters,
    resetFilters,
    nonEmptyFilters,
  };
}

export default useFilters;
